import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MessageText } from "@/components/ui/message/index"
import {
  canTranslateInBrowser,
  translateIncomingMessageToEnglish,
} from "@/lib/browserMessageTranslation"

const englishCache = new Map<string, string>()
const MAX_CACHED_TRANSLATIONS = 100

const cacheTranslation = (key: string, translation: string) => {
  if (englishCache.size >= MAX_CACHED_TRANSLATIONS) {
    englishCache.delete(englishCache.keys().next().value!)
  }
  englishCache.set(key, translation)
}

type Props = {
  chatJid: string
  messageId: string
  original: string
  polish: string
}

export default function IncomingTranslation({
  chatJid,
  messageId,
  original,
  polish,
}: Props) {
  const { t } = useTranslation()
  const cacheKey = JSON.stringify([chatJid, messageId, original, polish])
  const [language, setLanguage] = useState<"pl" | "en">("pl")
  const [english, setEnglish] = useState(() => englishCache.get(cacheKey))
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState<number>()
  const [error, setError] = useState(false)
  const available = canTranslateInBrowser()

  const showEnglish = () => {
    if (english !== undefined) {
      setLanguage("en")
      return
    }

    setLoading(true)
    setProgress(undefined)
    setError(false)
    // The call begins synchronously, while this click's user activation lasts.
    void translateIncomingMessageToEnglish(original, polish, setProgress)
      .then((result) => {
        cacheTranslation(cacheKey, result)
        setEnglish(result)
        setLanguage("en")
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  return (
    <div className="mt-2">
      <div
        role="group"
        aria-label={t("chat.incomingTranslation.language")}
        className="mb-1 flex items-center gap-1 text-xs"
      >
        <button
          type="button"
          aria-pressed={language === "pl"}
          className={
            language === "pl"
              ? "font-semibold text-violet-600"
              : "text-blue-600 hover:underline"
          }
          onClick={() => setLanguage("pl")}
        >
          {t("chat.incomingTranslation.polishSlickAI")}
        </button>
        <span aria-hidden="true" className="text-muted-foreground">
          /
        </span>
        <button
          type="button"
          aria-pressed={language === "en"}
          className={
            language === "en"
              ? "font-semibold text-violet-600"
              : "text-blue-600 hover:underline"
          }
          disabled={!available || loading}
          title={
            !available ? t("chat.incomingTranslation.unsupported") : undefined
          }
          onClick={showEnglish}
        >
          {t("chat.incomingTranslation.englishBrowser")}
        </button>
      </div>
      <MessageText
        content={language === "en" ? english : polish}
        className="text-violet-600"
      />
      {loading && (
        <div role="status" className="mt-1 text-xs text-muted-foreground">
          {progress === undefined
            ? t("chat.incomingTranslation.translating")
            : t("chat.incomingTranslation.downloadingModel", { progress })}
        </div>
      )}
      {error && (
        <div role="alert" className="mt-1 text-xs text-destructive">
          {t("chat.incomingTranslation.failed")}
        </div>
      )}
    </div>
  )
}
